import { tokens } from './tokens';

/** Spotify brand layer — sits on the shared dark shell without changing app primary. */
export const spotifyTokens = {
  green: '#1DB954',
  greenHover: '#1ed760',
  greenMuted: 'rgba(29, 185, 84, 0.18)',
  greenSoft: 'rgba(29, 185, 84, 0.28)',
  surface: '#282828',
  surfaceDeep: '#181818',
  border: '#404040',
  borderSubtle: '#333333',
  text: '#ffffff',
  textMuted: '#b3b3b3',
  danger: '#ff4444',
  dangerBg: 'rgba(255, 68, 68, 0.1)',
  gradient: 'linear-gradient(90deg, #1DB954 0%, #1ed760 100%)',
} as const;

/** Green gradient title — Spotify counterpart to brandTitleSx. */
export const spotifyTitleSx = {
  background: spotifyTokens.gradient,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  textFillColor: 'transparent',
  fontWeight: 700,
  letterSpacing: '-0.02em',
  textWrap: 'balance' as const,
};

/** Pill Spotify CTA (Login / Generate / etc.). */
export const spotifyCtaSx = {
  bgcolor: spotifyTokens.green,
  color: '#fff',
  fontWeight: 700,
  textTransform: 'none' as const,
  borderRadius: `${tokens.radii.pill}px`,
  px: 3,
  py: 1.25,
  minHeight: 44,
  boxShadow: '0 4px 14px rgba(29, 185, 84, 0.28)',
  transitionProperty: 'transform, background-color, box-shadow',
  transitionDuration: '150ms',
  transitionTimingFunction: 'cubic-bezier(0.2, 0, 0, 1)',
  '&:hover': {
    bgcolor: spotifyTokens.greenHover,
    boxShadow: '0 6px 18px rgba(29, 185, 84, 0.4)',
  },
  '&:active': { transform: 'scale(0.96)' },
  '&.Mui-disabled': {
    bgcolor: 'rgba(29, 185, 84, 0.35)',
    color: 'rgba(255,255,255,0.7)',
  },
};

/** Spotify-elevated text fields (#282828 + green focus). */
export const spotifyFieldSx = {
  width: '100%',
  maxWidth: 800,
  '& .MuiOutlinedInput-root': {
    color: spotifyTokens.text,
    backgroundColor: spotifyTokens.surface,
    borderRadius: 2,
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: spotifyTokens.green,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: spotifyTokens.green,
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: spotifyTokens.border,
  },
  '& .MuiInputLabel-root': {
    color: spotifyTokens.textMuted,
    '&.Mui-focused': {
      color: spotifyTokens.green,
    },
  },
  '& .MuiFormHelperText-root': {
    color: spotifyTokens.textMuted,
  },
  '& .MuiSelect-icon': {
    color: spotifyTokens.textMuted,
  },
} as const;

/** Result / auth cards. */
export const spotifyPanelSx = {
  width: '100%',
  maxWidth: 800,
  p: { xs: 2, sm: 2.5 },
  bgcolor: spotifyTokens.surfaceDeep,
  backgroundImage: 'none',
  borderRadius: `${tokens.radii.card}px`,
  border: `1px solid ${spotifyTokens.borderSubtle}`,
  boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
} as const;

export const spotifyLinkSx = {
  color: spotifyTokens.green,
  textDecoration: 'underline',
  textUnderlineOffset: 3,
  cursor: 'pointer',
  fontWeight: 500,
  border: 0,
  background: 'none',
  backgroundColor: 'transparent',
  boxShadow: 'none',
  p: 0,
  font: 'inherit',
  '&:hover': { color: spotifyTokens.greenHover, background: 'none' },
  '&:focus': { background: 'none' },
  '&:active': { background: 'none' },
} as const;

export const spotifyMutedSx = {
  color: spotifyTokens.textMuted,
} as const;

export const spotifyProgressSx = {
  color: spotifyTokens.green,
} as const;

/** Dark DataGrid that matches Spotify tool chrome. */
export const spotifyDataGridSx = {
  border: `1px solid ${spotifyTokens.borderSubtle}`,
  borderRadius: `${tokens.radii.card}px`,
  bgcolor: spotifyTokens.surfaceDeep,
  color: spotifyTokens.text,
  '& .MuiDataGrid-columnHeaders': {
    bgcolor: spotifyTokens.surface,
    borderBottom: `1px solid ${spotifyTokens.borderSubtle}`,
  },
  '& .MuiDataGrid-cell': {
    borderColor: spotifyTokens.borderSubtle,
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: `1px solid ${spotifyTokens.borderSubtle}`,
    bgcolor: spotifyTokens.surface,
  },
  '& .MuiDataGrid-row:hover': {
    bgcolor: 'rgba(29, 185, 84, 0.06)',
  },
  '& .MuiCheckbox-root.Mui-checked': {
    color: spotifyTokens.green,
  },
} as const;
